import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface GaugeChartProps {
  score: number;
  maxScore?: number;
}

const GaugeChart = ({ score, maxScore = 100 }: GaugeChartProps) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  
  const percentage = (animatedScore / maxScore) * 100;
  const rotation = (percentage / 100) * 180 - 90; // -90 to 90 degrees
  
  // Color palette aligned with luxury theme
  const getColor = () => {
    if (percentage < 30) return "#FF9FBD"; // Pink for all - brand consistent
    if (percentage < 70) return "#FF9FBD";
    return "#FF9FBD";
  };

  const getGlowColor = () => {
    if (percentage < 30) return "rgba(255, 159, 189, 0.5)";
    if (percentage < 70) return "rgba(255, 159, 189, 0.6)";
    return "rgba(255, 159, 189, 0.7)";
  };

  const getLabelColor = () => {
    if (percentage < 30) return "#8B0030"; // Deep burgundy - low score
    if (percentage < 70) return "#C4507A"; // Mid rose - medium score  
    return "#FF9FBD"; // Bright pink - high score
  };

  const getLabel = () => {
    if (percentage < 30) return "Estructura sin Valor";
    if (percentage < 70) return "Activo Desaprovechado";
    return "Activo de Alto Valor";
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedScore(score);
    }, 500);
    return () => clearTimeout(timer);
  }, [score]);

  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Outer glow effect with pulse animation */}
      <motion.div
        className="absolute inset-0 rounded-full blur-3xl"
        style={{ backgroundColor: getGlowColor() }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: [0, 0.4, 0.2, 0.5, 0.3],
          scale: [0.8, 1.1, 1, 1.05, 1]
        }}
        transition={{ 
          duration: 3,
          delay: 2,
          times: [0, 0.3, 0.5, 0.7, 1],
          repeat: Infinity,
          repeatDelay: 1
        }}
      />

      <svg viewBox="0 0 200 130" className="w-full relative z-10">
        <defs>
          {/* Elegant gradients - from light (0%) to dark (100%) */}
          <linearGradient id="arcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FCC3D7" />
            <stop offset="50%" stopColor="#8B0030" />
            <stop offset="100%" stopColor="#470628" />
          </linearGradient>
          
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FCC3D7" />
            <stop offset="35%" stopColor="#C4507A" />
            <stop offset="70%" stopColor="#8B0030" />
            <stop offset="100%" stopColor="#470628" />
          </linearGradient>

          <linearGradient id="needleGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FCC3D7" />
            <stop offset="100%" stopColor="#FF9FBD" />
          </linearGradient>

          {/* Sophisticated glow filter */}
          <filter id="elegantGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur"/>
            <feFlood floodColor="#FF9FBD" floodOpacity="0.5"/>
            <feComposite in2="blur" operator="in"/>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          {/* Subtle shadow for depth */}
          <filter id="shadowFilter" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.3"/>
          </filter>
        </defs>

        {/* Outer decorative ring */}
        <path
          d="M 15 105 A 85 85 0 0 1 185 105"
          fill="none"
          stroke="rgba(255, 159, 189, 0.1)"
          strokeWidth="1"
        />

        {/* Background arc with elegant styling */}
        <path
          d="M 20 105 A 80 80 0 0 1 180 105"
          fill="none"
          stroke="rgba(71, 6, 40, 0.4)"
          strokeWidth="16"
          strokeLinecap="round"
        />

        {/* Tick marks for elegance */}
        {[0, 25, 50, 75, 100].map((tick, i) => {
          const angle = ((tick / 100) * 180 - 180) * (Math.PI / 180);
          const x1 = 100 + 88 * Math.cos(angle);
          const y1 = 105 + 88 * Math.sin(angle);
          const x2 = 100 + 95 * Math.cos(angle);
          const y2 = 105 + 95 * Math.sin(angle);
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="rgba(255, 159, 189, 0.3)"
              strokeWidth="1"
            />
          );
        })}

        {/* Active progress arc */}
        <motion.path
          d="M 20 105 A 80 80 0 0 1 180 105"
          fill="none"
          stroke="url(#progressGradient)"
          strokeWidth="16"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: percentage / 100 }}
          transition={{ duration: 2.5, ease: "easeOut", delay: 0.5 }}
          filter="url(#elegantGlow)"
        />

        {/* Inner decorative circle */}
        <circle 
          cx="100" 
          cy="105" 
          r="55" 
          fill="none" 
          stroke="rgba(255, 159, 189, 0.08)" 
          strokeWidth="1"
        />

        {/* Needle with premium design - originates from center */}
        <motion.g
          initial={{ rotate: -90 }}
          animate={{ rotate: rotation }}
          transition={{ duration: 2.5, ease: "easeOut", delay: 0.5 }}
          style={{ transformOrigin: "100px 105px" }}
        >
          {/* Needle shadow */}
          <polygon
            points="100,35 97,102 100,108 103,102"
            fill="rgba(0,0,0,0.2)"
            transform="translate(2, 2)"
          />
          {/* Main needle - starts from center point */}
          <polygon
            points="100,35 97,102 100,108 103,102"
            fill="url(#needleGradient)"
            filter="url(#shadowFilter)"
          />
          {/* Needle highlight */}
          <polygon
            points="100,40 98.5,98 100,102 100,40"
            fill="rgba(255,255,255,0.3)"
          />
        </motion.g>

        {/* Center jewel design */}
        <circle cx="100" cy="105" r="14" fill="#470628" />
        <circle cx="100" cy="105" r="11" fill="url(#needleGradient)" />
        <circle cx="100" cy="105" r="6" fill="#470628" />
        <circle cx="100" cy="105" r="3" fill="#FF9FBD" />

        {/* Elegant labels */}
        <text 
          x="18" 
          y="122" 
          fill="rgba(255, 159, 189, 0.5)" 
          fontSize="9" 
          fontFamily="system-ui"
        >
          0
        </text>
        <text 
          x="93" 
          y="16" 
          fill="rgba(255, 159, 189, 0.5)" 
          fontSize="9" 
          fontFamily="system-ui"
        >
          50
        </text>
        <text 
          x="172" 
          y="122" 
          fill="rgba(255, 159, 189, 0.5)" 
          fontSize="9" 
          fontFamily="system-ui"
        >
          100
        </text>
      </svg>

      {/* Score display with premium styling */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2 }}
        className="text-center mt-4"
      >
        <motion.div
          className="inline-flex items-baseline gap-1"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 2.2 }}
        >
          <span
            className="text-6xl font-display font-bold tracking-tight"
            style={{ 
              color: getColor(),
              textShadow: `0 0 30px ${getGlowColor()}`
            }}
          >
            {animatedScore}
          </span>
          <span className="text-2xl text-white/40 font-light">
            /{maxScore}
          </span>
        </motion.div>
        
        {/* Score label with brand colors */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 0.6 }}
          className="mt-4 px-6 py-2 rounded-full"
          style={{ 
            backgroundColor: `${getLabelColor()}20`,
            border: `1px solid ${getLabelColor()}40`
          }}
        >
          <p
            className="text-sm font-semibold tracking-wider uppercase"
            style={{ color: getLabelColor() }}
          >
            {getLabel()}
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default GaugeChart;
